import { createSlice } from "@reduxjs/toolkit";

let nextId = 2; // since initial flight uses ID 1

const initFlight = (id) => ({
  product_flight_id: id,
  origin_code: "",
  destination_code: "",
  trip: "OW",
  itinerary_count: 0,
  adult: 0,
  child: 0,
  infant: 0,
  luggage_cout: 0,
  luggage_type: "kg",
});

const defaultSegment = () => ({
  ref: null,
  flight_number: "",
  airline_code: "",
  class: "",
  departure_airport_code: "",
  departure_at: "",
  departure_terminal: "",
  arrival_airport_code: "",
  arrival_at: "",
  arrival_terminal: "",
  duration: "",
  is_has_wifi: false,
  meal_fight_ids: [],
  entertainment_fight_ids: [],
});

const initialState = {
  step1: {
    airline_code: "",
    country_id: "",
    city_id: null,
    product_type_id: 1,
    flight_type_id: null,
    address: "",
    latitude: "",
    longitude: "",
    destinations: [{ departure_country_id: "", arrival_country_id: "" }],
  },
  step2: {
    flight: [initFlight(1)],
  },
  step3: {
    flight: [
      {
        product_flight_id: 1,
        itineraries: {
          product_flight_itinerary_id: 1,
          segments: [{ ...defaultSegment(), ref: 1 }],
        },
      },
    ],
  },
  step4: {
    cancellation_policy_id: "",
    booking_confirmation_mode: "",
    charge_service: null,
    charge_service_type: null,
    flight: [
      {
        product_flight_id: 1,
        product_flight_itinerary_id: 1,
        pricing: { adult: 0, child: 0, infant: 0 },
      },
    ],
  },
};

const setNested = (obj, path, value) => {
  const keys = path.split(".");
  let target = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!target[keys[i]]) return;
    target = target[keys[i]];
  }
  target[keys[keys.length - 1]] = value;
};

function deepUpdate(obj, field, value) {
  if (obj && typeof obj === "object") {
    if (field in obj) {
      obj[field] = value;
      return true;
    }
    for (const key of Object.keys(obj)) {
      if (deepUpdate(obj[key], field, value)) {
        return true;
      }
    }
  }
  return false;
}

const flightSlice = createSlice({
  name: "flight",
  initialState,
  reducers: {
    updateFlightStepField: (state, action) => {
      const { field, value } = action.payload;

      if (typeof field !== "string") {
        console.error("updateFlightStepField: invalid field", field);
        return;
      }

      // Dot notation support
      if (field.includes(".")) {
        const keys = field.split(".");
        let target = state;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
        return;
      }

      // Deep search fallback
      if (!deepUpdate(state, field, value)) {
        console.warn(`Field '${field}' not found in state`);
      }
    },

    updateFlightStepBulk: (state, { payload: { step, data } }) => {
      if (state[step]) state[step] = { ...state[step], ...data };
    },
    // ------------------------------
    // Destination logic
    // ------------------------------
    addDestination: (state, { payload = {} }) => {
      const id = nextId++;

      state.step1.destinations.push({
        departure_country_id: "",
        arrival_country_id: "",
        ...payload,
      });

      state.step2.flight.push(initFlight(id));

      state.step3.flight.push({
        product_flight_id: id,
        itineraries: {
          product_flight_itinerary_id: id,
          segments: [{ ...defaultSegment(), ref: 1 }],
        },
      });

      state.step4.flight.push({
        product_flight_id: id,
        product_flight_itinerary_id: id,
        pricing: { adult: 0, child: 0, infant: 0 },
      });
    },

    updateDestination: (state, { payload: { index, data } }) => {
      if (state.step1.destinations[index]) {
        state.step1.destinations[index] = {
          ...state.step1.destinations[index],
          ...data,
        };
      }
    },

    deleteDestination: (state, { payload: index }) => {
      if (index >= 0 && index < state.step1.destinations.length) {
        state.step1.destinations.splice(index, 1);
        state.step2.flight.splice(index, 1);
        state.step3.flight.splice(index, 1);
        state.step4.flight.splice(index, 1);
      }
    },

    // ------------------------------
    // Step 2 flight updates
    // ------------------------------
    updateFlightField: (state, { payload: { index, field, value } }) => {
      const flight = state.step2.flight?.[index];
      if (!flight) return;

      if (field.includes(".")) {
        setNested(flight, field, value);
      } else {
        flight[field] = value;

        // If itinerary_count changes, update step3 segments
        if (field === "itinerary_count") {
          const segments =
            state.step3.flight?.[index]?.itineraries?.segments || [];

          const newCount = Number(value) || 0;

          if (segments.length < newCount) {
            // add missing segments
            for (let i = segments.length; i < newCount; i++) {
              segments.push({ ...defaultSegment(), ref: i + 1 });
            }
          } else if (segments.length > newCount) {
            // remove extra segments
            state.step3.flight[index].itineraries.segments = segments
              .slice(0, newCount)
              .map((seg, i) => ({ ...seg, ref: i + 1 }));
          }
        }
      }
    },

    updateSegment: (state, { payload: { flightIndex, ref, data } }) => {
      const segments = state.step3.flight[flightIndex]?.itineraries?.segments;
      if (!segments) return;
      const idx = segments.findIndex((s) => s.ref === ref);
      if (idx !== -1) segments[idx] = { ...segments[idx], ...data };
    },

    // ------------------------------
    // Step 4 pricing updates
    // ------------------------------
    updatePricing: (state, { payload: { flightIndex, field, value } }) => {
      const pricing = state.step4.flight?.[flightIndex]?.pricing;
      if (!pricing) return;
      pricing[field] = value;
    },

    // ------------------------------
    // Reset all
    // ------------------------------
    resetFlight: () => {
      nextId = 2;
      return initialState;
    },
  },
});

export const {
  updateFlightStepField,
  updateFlightStepBulk,
  addDestination,
  updateDestination,
  deleteDestination,
  updateFlightField,
  updateSegment,
  updatePricing,
  resetFlight,
} = flightSlice.actions;

export default flightSlice.reducer;
